import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "./Header/Header";
import ListMenu from "./ListMenu/ListMenu";
import SummaryCard from "./SummaryCard";
import Backend from "../../../../Backend/Backend";
import useSize from "../../../../Utils/useSize/useSize";
import toast from "react-hot-toast";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";
import Total from "./Total/Total";

import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
const fmt = require("indian-number-format");

export default function Summary({ super_id, userType }) {
  const windowSize = useSize();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [backend, setBackend] = useState(null);

  /** 🔹 Initialize Backend once */
  useEffect(() => {
    (async () => {
      const b = await new Backend().init();
      setBackend(b);
    })();
  }, []);

  /** 🧠 Load summary data */
  const loadSummary = useCallback(async () => {
    if (!backend) return;

    try {
      setLoading(true);
      setSummaryData({});
      setUsers([]);

      const baseData = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
      };

      const data =
        userType === 1
          ? super_id
            ? { ...baseData, super_id }
            : { ...baseData, admin_id: baseData.id }
          : { ...baseData, super_id: baseData.id };

      const res = await backend.load_summary_master(data);

      if (res.error === "False") {
        setUsers(res.user || []);
      } else {
        res.logout === 1 ? window.LogOut?.() : toast.error(res.message);
      }
    } catch (err) {
      console.error("LoadSummary Error:", err);
      toast.error("Failed to load summary. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [backend, super_id, userType]);

  /** 🔄 Refresh on dependency change */
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /** 🧮 Compute totals */
  const totalSummary = useMemo(() => {
    const all = Object.values(summaryData);
    if (all.length === 0) return null;

    const sum = (key) =>
      all.reduce((acc, curr) => acc + parseFloat(curr[key] || 0), 0);

    return {
      booked: sum("booked"),
      total: sum("total"),
      net_total: sum("net_total"),
      upper: sum("upper"),
      self: sum("self"),
      lower: sum("lower"),
      upperLive: sum("upperLive"),
      selfLive: sum("selfLive"),
      lowerLive: sum("lowerLive"),
    };
  }, [summaryData]);

  /** 📥 Child summary callback */
  const handleSummaryCalculated = useCallback((data) => {
    setSummaryData((prev) => ({
      ...prev,
      [data.master_id]: data,
    }));
  }, []);

  /** 📤 Excel Export */
  const downloadExcel = useCallback(() => {
    if (!summaryData || Object.keys(summaryData).length === 0) {
      toast.error("No data to export");
      return;
    }

    const rows = Object.values(summaryData).map((item) => {
      const row = {
        Master: item.master_username,
        Booked: fmt.format(item.booked || 0),
        "Live Total": fmt.format(item.total || 0),
        "Net Live Total": fmt.format(item.net_total || 0),
        "Upper Share": item.shares?.upper ?? "-",
        "Self Share": item.shares?.self ?? "-",
        "Lower Share": item.shares?.lower ?? "-",
        "Upper (Combined)": fmt.format(item.upper || 0),
        "Self (Combined)": fmt.format(item.self || 0),
        "Lower (Combined)": fmt.format(item.lower || 0),
        "Upper Live": fmt.format(item.upperLive || 0),
        "Self Live": fmt.format(item.selfLive || 0),
        "Lower Live": fmt.format(item.lowerLive || 0),
      };

      if (userType === 1) row.Super = item.super_username;
      return row;
    });

    if (totalSummary) {
      const totalRow = {
        Master: "TOTAL",
        Booked: fmt.format(totalSummary.booked),
        "Live Total": fmt.format(totalSummary.total),
        "Net Live Total": fmt.format(totalSummary.net_total),
        "Upper (Combined)": fmt.format(totalSummary.upper),
        "Self (Combined)": fmt.format(totalSummary.self),
        "Lower (Combined)": fmt.format(totalSummary.lower),
        "Upper Live": fmt.format(totalSummary.upperLive),
        "Self Live": fmt.format(totalSummary.selfLive),
        "Lower Live": fmt.format(totalSummary.lowerLive),
      };

      if (userType === 1) totalRow.Super = "TOTAL";
      rows.push(totalRow);
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Master-Summary-${moment().format("DD-MMM-YYYY")}.xlsx`);
  }, [summaryData, totalSummary, userType]);

  /** 📄 PDF Export */
  const downloadPDF = useCallback(async () => {
    if (!summaryData || Object.keys(summaryData).length === 0) {
      toast.error("No data available to export");
      return;
    }

    try {
      const pdfMakeModule = await import("pdfmake/build/pdfmake");
      const pdfFontsModule = await import("pdfmake/build/vfs_fonts");
      const pdfMake = pdfMakeModule.default || pdfMakeModule;
      const pdfFonts = pdfFontsModule.default || pdfFontsModule;
      pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

      const headers = [
        ...(userType === 1 ? ["Super"] : []),
        "Master",
        "Booked",
        "Total",
        "NetTotal",
        "Upper",
        "Self",
        "Lower",
        "Upper Live",
        "Self Live",
        "Lower Live",
      ];

      const body = [
        headers,
        ...Object.values(summaryData).map((i) => [
          ...(userType === 1 ? [i.super_username] : []),
          i.master_username,
          fmt.format(i.booked),
          fmt.format(i.total),
          fmt.format(i.net_total),
          fmt.format(i.upper),
          fmt.format(i.self),
          fmt.format(i.lower),
          fmt.format(i.upperLive),
          fmt.format(i.selfLive),
          fmt.format(i.lowerLive),
        ]),
      ];

      if (totalSummary) {
        body.push([
          ...(userType === 1 ? ["TOTAL"] : []),
          "TOTAL",
          fmt.format(totalSummary.booked),
          fmt.format(totalSummary.total),
          fmt.format(totalSummary.net_total),
          fmt.format(totalSummary.upper),
          fmt.format(totalSummary.self),
          fmt.format(totalSummary.lower),
          fmt.format(totalSummary.upperLive),
          fmt.format(totalSummary.selfLive),
          fmt.format(totalSummary.lowerLive),
        ]);
      }

      const docDefinition = {
        pageSize: "A3",
        pageOrientation: "landscape",
        content: [
          {
            text: `${window.APP_NAME} MASTER REPORT`,
            style: "header",
            alignment: "center",
          },
          {
            text: `Generated on: ${moment().format("DD-MMM-YYYY HH:mm")}`,
            style: "subHeader",
          },
          { table: { headerRows: 1, body } },
        ],
        styles: {
          header: { fontSize: 18, bold: true, margin: [0, 0, 0, 10] },
          subHeader: { fontSize: 10, italics: true, margin: [0, 0, 0, 10] },
        },
      };

      pdfMake
        .createPdf(docDefinition)
        .download(`${moment().format("DD-MMM")}-Master-Summary.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    }
  }, [summaryData, totalSummary, userType]);

  /** 🧱 Render */
  return (
    <div>
      <Header
        records={users.length}
        downloadExcel={downloadExcel}
        downloadPDF={downloadPDF}
      />
      <ListMenu userType={userType} />

      <div
        style={{
          width: windowSize[0],
          height: windowSize[1] - 240,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Loading height={windowSize[1] - 240} />
        ) : users.length > 0 ? (
          users.map((item, index) => (
            <SummaryCard
              userType={userType}
              key={item.master_id || index}
              item={item}
              index={index}
              handleSummaryCalculated={handleSummaryCalculated}
            />
          ))
        ) : (
          <NotFound
            height={windowSize[1] - 240}
            title="Nothing here"
            subtext="No User Summary Found."
          />
        )}
      </div>

      <Total totalSummary={totalSummary} userType={userType} />
    </div>
  );
}
